import { fetchClient } from "@/api/api";
import type {
	Actions,
	buyerOrdersInput,
	buyerOrdersOutput,
	createOrderInput,
	createOrderOutput,
	getAllOrdersInput,
	getAllOrdersOutput,
	getOneOrderInput,
	getOneOrderOutput,
	getOrderTransferRecordsInput,
	getOrderTransferRecordsOutput,
	getOrdersFacetsOutput,
	orderActionInput,
	orderActionOutput,
	sellerOrdersInput,
	sellerOrdersOutput,
	sellersForOrderInput,
	sellersForOrderOutput,
	updateOrderInput,
	updateOrderOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { createFacetSource } from "@/services/facets";
import { invalidateDebtQueries } from "@/services/invalidateDebt";
import { normalizeEmptyStringsToNull } from "@/utils/normalize";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ORDER_SERVICE_KEYS } from "./keys";

const useGetOrders: CustomQueryHook<getAllOrdersOutput, getAllOrdersInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: ORDER_SERVICE_KEYS.useGetOrders(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/marketplace/orders/", {
				params: { query: payload?.query },
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		refetchInterval: 20_000,
	});
};

const useGetSellerOrders: CustomQueryHook<
	sellerOrdersOutput,
	sellerOrdersInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: ORDER_SERVICE_KEYS.useGetSellerOrders(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/seller/orders/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		refetchInterval: 20_000,
	});
};

const useGetBuyerOrders: CustomQueryHook<buyerOrdersOutput, buyerOrdersInput> =
	(payload, options) => {
		return useQuery({
			...options,
			queryKey: ORDER_SERVICE_KEYS.useGetBuyerOrders(payload),
			queryFn: async () => {
				const { data, error } = await fetchClient.GET(
					"/v1/marketplace/dashboard/buyer/orders/",
					{ params: { query: payload?.query }, throwOnError: true },
				);
				if (error) throw error;
				return data;
			},
			refetchInterval: 20_000,
		});
	};

const useSellersForOrder: CustomQueryHook<
	sellersForOrderOutput,
	sellersForOrderInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: ORDER_SERVICE_KEYS.useSellersForOrder(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/seller-recommendations/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useCreateOrder: CustomMutationHook<createOrderInput, createOrderOutput> =
	() => {
		const { toastApiError } = useApiError();
		const { t } = useTranslation();

		return useMutation({
			mutationKey: ORDER_SERVICE_KEYS.useCreateOrder(),
			mutationFn: async (payload) => {
				const { data, error } = await fetchClient.POST(
					"/v1/marketplace/orders/",
					{
						body: normalizeEmptyStringsToNull(payload.body),
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},

			onError: toastApiError,
			onSuccess: () => {
				toast.success(t("addOrder.successMessage"));
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetBuyerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetSellerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrdersFacet(),
				});
				invalidateDebtQueries();
			},
		});
	};

const useGetOrderById: CustomQueryHook<getOneOrderOutput, getOneOrderInput> = (
	payload,
	options,
) => {
	return useQuery({
		...options,
		queryKey: ORDER_SERVICE_KEYS.useGetOrderById(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/orders/{order_id}",
				{ params: { path: payload.path }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
	});
};

const useGetOrderTransferRecord: CustomQueryHook<
	getOrderTransferRecordsOutput,
	getOrderTransferRecordsInput
> = (payload, options) => {
	return useQuery({
		...options,

		queryKey: ORDER_SERVICE_KEYS.useGetOrderTransferRecord(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/dashboard/orders/{order_id}/transfer-records/",
				{
					params: { path: payload.path, query: payload.query },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
	});
};

const useOrderAction: CustomMutationHook<orderActionInput, orderActionOutput> =
	() => {
		const { toastApiError } = useApiError();

		return useMutation({
			mutationKey: ORDER_SERVICE_KEYS.useOrderAction(),
			mutationFn: async (payload) => {
				const { data, error } = await fetchClient.POST(
					"/v1/marketplace/orders/{order_id}",
					{
						params: { path: payload.path, query: payload.query },
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},
			onError: toastApiError,
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrderById(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrderTransferRecord(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetSellerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetBuyerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrdersFacet(),
				});
				invalidateDebtQueries();
			},
		});
	};

const useBatchOrderAction = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: ORDER_SERVICE_KEYS.useBatchOrderAction(),
		mutationFn: async ({
			action,
			orderIds,
		}: { action: Actions; orderIds: string[] }) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/orders/batch-action/",
				{
					params: { query: { action } },
					body: orderIds,
					throwOnError: true,
				},
			);
			if (error) throw error;
			// ids of orders whose status no longer allowed the action
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrders(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrderById(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrderTransferRecord(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetSellerOrders(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetBuyerOrders(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrdersFacet(),
			});
			invalidateDebtQueries();
		},
	});
};

const useUpdateOrder: CustomMutationHook<updateOrderInput, updateOrderOutput> =
	() => {
		const { toastApiError } = useApiError();

		return useMutation({
			mutationKey: ORDER_SERVICE_KEYS.useUpdateOrder(),
			mutationFn: async (payload) => {
				const { data, error } = await fetchClient.PATCH(
					"/v1/marketplace/orders/{order_id}",
					{
						params: { path: payload.path },
						body: normalizeEmptyStringsToNull(payload.body),
						throwOnError: true,
					},
				);
				if (error) throw error;
				return data;
			},
			onError: toastApiError,
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrderById(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrderTransferRecord(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetSellerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetBuyerOrders(),
				});
				queryClient.invalidateQueries({
					queryKey: ORDER_SERVICE_KEYS.useGetOrdersFacet(),
				});
				invalidateDebtQueries();
			},
		});
	};

const ORDERS_FACET_SOURCE = createFacetSource<getOrdersFacetsOutput>({
	path: "/v1/marketplace/orders/facets/",
	queryKey: (payload) => ORDER_SERVICE_KEYS.useGetOrdersFacet(payload as any),
});

export {
	ORDERS_FACET_SOURCE,
	useGetOrderTransferRecord,
	useGetOrderById,
	useGetOrders,
	useGetSellerOrders,
	useGetBuyerOrders,
	useSellersForOrder,
	useCreateOrder,
	useOrderAction,
	useBatchOrderAction,
	useUpdateOrder,
};
